import React, { useMemo } from 'react';
import { useIdeaFilters } from '../../../hooks/useIdeaFilters';
import ExploreFilter from './ExploreFilter';

interface ExploreDiscoveryFiltersProps {
  sessionId?: string | null;
  onFiltersApplied?: (sessionId: string) => void;
}

function stableSegmentChildKey(f: { name?: string; parent?: string }): string {
  return `${f.name ?? 'filter'}__${f.parent ?? ''}`;
}

/**
 * 1. Sources — evidence filter named 'businessName' ("All sources").
 * 2. All segments — one menu with a flat list of segment filters (ChildMenu per row).
 */
const ExploreDiscoveryFilters: React.FC<ExploreDiscoveryFiltersProps> = ({ sessionId, onFiltersApplied }) => {
  const { ideasFiltersConfig } = useIdeaFilters();

  // businessName can live at the top level or nested inside a parent-type evidence filter
  const sourceFilter: any = useMemo(() => {
    const evidenceFilters = Object.values(ideasFiltersConfig?.evidence ?? {}) as any[];
    let found = evidenceFilters.find((f: any) => f?.name === 'businessName') ?? null;
    if (!found) {
      for (const f of evidenceFilters) {
        if (f?.type === 'parent' && f?.childFilters) {
          const children = Object.values(f.childFilters) as any[];
          found = children.find((c: any) => c?.name === 'businessName') ?? null;
          if (found) break;
        }
      }
    }
    return found;
  }, [ideasFiltersConfig]);

  const flatDiscoveryFilters: any[] = useMemo(() => {
    const topLevelFilters = [
      ...Object.values(ideasFiltersConfig?.evidence ?? {}),
      ...Object.values(ideasFiltersConfig?.idea ?? {}),
    ] as any[];

    const result: any[] = [];
    for (const filter of topLevelFilters) {
      const isParent = filter?.type === 'parent';
      if (!isParent) {
        if (filter?.showInDiscovery === true) result.push(filter);
        continue;
      }
      const children = Object.values(filter?.childFilters ?? {}) as any[];
      const markedChildren = children.filter((c: any) => c?.showInDiscovery === true);
      if (markedChildren.length > 0) {
        result.push(...markedChildren);
      } else if (filter?.showInDiscovery === true) {
        result.push(...children);
      }
    }
    return result;
  }, [ideasFiltersConfig]);

  const segmentFilters = useMemo(
    () => flatDiscoveryFilters.filter((f: any) => f?.name !== 'businessName'),
    [flatDiscoveryFilters]
  );

  const segmentsParent: any | null = useMemo(() => {
    if (segmentFilters.length === 0) return null;
    const childFilters = Object.fromEntries(
      segmentFilters.map((f: any) => [stableSegmentChildKey(f), f])
    );
    return {
      type: 'parent',
      label: 'All segments',
      name: 'discovery-segments',
      parent: 'discovery-segments',
      filterObject: 'evidence',
      childFilters,
    };
  }, [segmentFilters]);

  return (
    <>
      {sourceFilter && (
        <ExploreFilter
          variant="source"
          filterType="dynamic"
          dynamicFilterConfig={{ ...sourceFilter, label: 'All sources' }}
          sessionId={sessionId}
          onFiltersApplied={onFiltersApplied}
          isNewChat
        />
      )}
      {segmentsParent && (
        <ExploreFilter
          variant="segments"
          filterType="dynamic"
          dynamicFilterConfig={segmentsParent}
          sessionId={sessionId}
          onFiltersApplied={onFiltersApplied}
          isNewChat
        />
      )}
    </>
  );
};

export default ExploreDiscoveryFilters;
